import { CONFIG } from '../config';
import { localHeadline } from '../content/headlines';
import {
  bestFit,
  ENERGY_DRINKS,
  FOOD_HUNGER,
  FOOD_PRIORITY,
  ITEMS,
  LAMP_ITEMS,
  SCRATCH_ITEMS,
} from '../content/items';
import { byBody } from '../content/looks';
import { drawMail } from '../content/mail';
import type { Choice, Scene } from '../content/types';
import type { NpcAgent } from '../entities/NpcManager';
import { generateScratchTicket, isNight, SCRATCH_GAMES } from '../state/rules';
import type { ItemId } from '../state/types';
import { pickShow } from '../content/tv';
import { TvStage } from '../stages/TvStage';
import type { Room } from '../world/Room';
import type { Driving } from './Driving';
import type { RestCycle } from './RestCycle';
import type { GameServices } from './services';

type Spot = { x: number; z: number };

/** Everything the player can *do*: drink, eat, sit, soak, scratch, wrench on the Camaro... */
export class Activities {
  /**
   * Hustles get a word in: a neighbor waving you down for a favor takes over the whole
   * conversation; a delivery run or a shine run adds a choice to the usual ones.
   */
  readonly talkOverrides: Array<(agent: NpcAgent) => Scene | null> = [];
  readonly talkChoices: Array<(agent: NpcAgent) => Choice[]> = [];

  constructor(
    private readonly s: GameServices,
    private readonly rest: RestCycle,
    private readonly driving: Driving,
  ) {}

  canAct(): boolean {
    return !this.s.player.busy && !this.s.stunts.active && !this.rest.sleeping;
  }

  private float(text: string, color: string): void {
    this.s.ui.hud.float(text, this.s.player.headPosition(), color);
  }

  useItem(id: ItemId): void {
    if (id === 'beer') this.drinkBeer();
    else if (FOOD_HUNGER[id] !== undefined) this.eat(id);
    else if (ENERGY_DRINKS[id] !== undefined) this.drinkDewd(id);
    else if (LAMP_ITEMS.includes(id)) this.toggleLamp?.();
    else if (id === 'foilHat') this.toggleFoilHat?.();
    else if (SCRATCH_ITEMS.includes(id)) this.scratchTicket(id);
    else if (id === 'fireworks') this.lightFireworks();
    else if (id === 'cigs') this.smoke();
    else if (id === 'wine' || id === 'whiskey') this.drinkHard(id);
    else this.s.store.toast(`${ITEMS[id].icon} ${ITEMS[id].desc}`, 'info');
  }

  talk(agent: NpcAgent): void {
    const { store, player, rng, ctx, ui } = this.s;
    const def = agent.def;
    agent.faceToward(player.position.x, player.position.z);
    player.facing = Math.atan2(agent.position.x - player.position.x, agent.position.z - player.position.z);
    player.syncTransform();
    store.setFlag(`met_${def.id}`, true);
    store.incr('talks');
    for (const o of this.talkOverrides) {
      const scene = o(agent);
      if (scene) {
        ui.modal.show(scene, () => agent.endTalk());
        return;
      }
    }
    ui.modal.show(
      {
        title: def.name,
        speaker: { id: def.id, name: def.title, voice: def.voice },
        text: rng.pick(def.lines(ctx)),
        choices: [...this.talkChoices.flatMap((f) => f(agent)), ...def.choices(ctx)],
      },
      () => agent.endTalk(),
    );
  }

  drinkBeer(): boolean {
    const { store, player, rng } = this.s;
    if (this.driving.active) {
      store.toast("Don't drink and drive. Even in a video game, Bubba.", 'bad', ':no:');
      return false;
    }
    if (!this.canAct()) return false;
    if (!store.take('beer')) {
      store.toast("You're out of beer! Gas-N-Grub has 12-packs.", 'bad', ':beer:');
      this.s.sfx('error');
      return false;
    }
    store.incr('beers');
    store.give('cans', 1);
    this.s.sfx('canOpen');
    window.setTimeout(() => this.s.sfx('gulp'), 450);
    player.doBusy({
      pose: 'drink',
      until: this.s.now() + 1.6,
      onDone: () => {
        store.addStat('buzz', 16);
        store.addStat('energy', 2);
        store.addStat('hunger', 2);
        if (rng.chance(0.3)) {
          this.s.sfx('burp');
          this.float('*BURP*', '#c4a14a');
        } else this.float('+1 :empty-can:', '#d9dde0');
        if (store.stat('buzz') >= 100) this.rest.passOut('blackout');
      },
    });
    return true;
  }

  /** Strawberry Hill or a Fire Bawl shooter: like a beer, only more so. */
  drinkHard(id: 'wine' | 'whiskey'): void {
    const { store, player } = this.s;
    if (this.driving.active) {
      store.toast("Don't drink and drive. Even in a video game, Bubba.", 'bad', ':no:');
      return;
    }
    if (!this.canAct() || !store.take(id)) return;
    store.incr('beers');
    this.s.sfx('gulp');
    player.doBusy({
      pose: 'drink',
      until: this.s.now() + 1.6,
      onDone: () => {
        if (id === 'wine') {
          store.apply({ stats: { buzz: 35, hunger: 4 }, clout: 2 });
          this.float(':strawberry: Strawberry Hill :strawberry:', '#ff6b8a');
        } else {
          store.apply({ stats: { buzz: 22, energy: 6, health: -2 } });
          this.float(':fire: WOOO :fire:', '#ff8a3c');
        }
        if (store.stat('buzz') >= 100) this.rest.passOut('blackout');
      },
    });
  }

  /** A smoke break. Not good for you. The game knows. You know. */
  smoke(): void {
    const { store, player, rng } = this.s;
    if (!this.canAct() || this.driving.active || !store.take('cigs')) return;
    store.incr('smokes');
    player.doBusy({
      pose: 'drink',
      until: this.s.now() + 2.4,
      onDone: () => {
        store.apply({ stats: { energy: 6, health: -3, hunger: -3 }, minutes: 10 });
        this.float(':dash:', '#d9dde0');
        if (rng.chance(0.15))
          store.toast(
            rng.pick([
              'You cough like a lawnmower that won’t start. Classic.',
              'Ten minutes of staring at the highway. Productive.',
              'Darlene bums one off you. Now you’re friends. Sort of.',
            ]),
            'info',
            ':smokes:',
          );
      },
    });
  }

  /** Set by the game: turns your flashlight or headlamp on and off. */
  toggleLamp: (() => void) | null = null;
  /** Set by the game: Hank's tinfoil hat on and off. */
  toggleFoilHat: (() => void) | null = null;

  /** Eat something: what you pick, or whatever best fits how hungry you are. */
  eat(which?: ItemId): void {
    const { store, player, rng } = this.s;
    if (!this.canAct() || this.driving.active) return;
    const owned = FOOD_PRIORITY.filter((f) => store.has(f));
    const food = which && store.has(which) ? which : bestFit(owned, FOOD_HUNGER, 100 - store.stat('hunger'));
    if (!food) {
      store.toast('No food! Roller dogs at Gas-N-Grub, or bum one off Big Earl.', 'bad', ':hotdog:');
      this.s.sfx('error');
      return;
    }
    store.take(food);
    store.incr('meals');
    this.s.sfx('crunch');
    player.doBusy({
      pose: 'eat',
      until: this.s.now() + 1.4,
      onDone: () => {
        if (food === 'hotdog') {
          store.addStat('hunger', 35);
          if (rng.chance(0.08)) {
            store.addStat('health', -15);
            store.toast(
              'Roller grill roulette: you lost. Your guts are filing a complaint.',
              'bad',
              ':queasy:',
            );
          }
        } else if (food === 'porkRinds') {
          store.addStat('hunger', 15);
        } else if (food === 'tvDinner') {
          store.apply({ stats: { hunger: 60, health: 5 } });
        } else if (food === 'pizza') {
          store.apply({ stats: { hunger: 75, energy: 5 } });
          if (rng.chance(0.25))
            store.toast('You ate a whole pizza standing up in one go. A personal record.', 'meme', ':pizza:');
        } else if (food === 'chicken') {
          store.apply({ stats: { hunger: 100, health: 10 }, clout: 1 });
          store.toast(
            'Twelve pieces. You licked the bucket. Colonel would be proud.',
            'good',
            ':fried-chicken:',
          );
        } else {
          store.incr('sushi');
          store.addStat('hunger', 50);
          if (rng.chance(0.35)) {
            store.addStat('health', -30);
            store.toast('The gas station sushi has chosen violence.', 'bad', ':sushi:');
            this.s.sfx('fail');
          } else store.toast("The sushi was... fine? Don't push your luck.", 'good', ':sushi:');
        }
        this.float(`${ITEMS[food].icon} nom`, '#ffa94d');
      },
    });
  }

  /** An energy drink: what you pick, or the one that best fits how tired you are. */
  drinkDewd(which?: ItemId): void {
    const { store, player } = this.s;
    if (!this.canAct() || this.driving.active) return;
    const owned = (Object.keys(ENERGY_DRINKS) as ItemId[]).filter((d) => store.has(d));
    const drink =
      which && store.has(which) ? which : bestFit(owned, ENERGY_DRINKS, 100 - store.stat('energy'));
    if (!drink || !store.take(drink)) {
      store.toast('No energy drinks. Gas-N-Grub has Mtn Dewd and worse.', 'bad', ':mtn-dewd:');
      return;
    }
    this.s.sfx('canOpen');
    player.doBusy({
      pose: 'drink',
      until: this.s.now() + 1.4,
      onDone: () => {
        if (drink === 'rocketFuel') {
          store.apply({ stats: { energy: 85, buzz: -20, health: -8 } });
          this.float(':rocket: HEART GO BRRRR', '#ff8a3c');
        } else if (drink === 'bigEnergy') {
          store.apply({ stats: { energy: 55, buzz: -10, health: -3 } });
          this.float(':bolt::bolt: LIGHTNIN', '#8bea2b');
        } else {
          store.addStat('energy', 30);
          store.addStat('buzz', -6);
          this.float(':bolt: WIRED', '#8bea2b');
        }
      },
    });
  }

  /** T scratches a ticket. Got more than one kind? Pick. */
  scratchTicket(which?: ItemId): void {
    const { store, rng, ui } = this.s;
    if (!this.canAct()) return;
    const owned = SCRATCH_ITEMS.filter((id) => store.has(id));
    if (!which && owned.length > 1) {
      ui.modal.show({
        title: 'Which Ticket?',
        art: ':scratcher:',
        text: 'The lucky penny is ready. Which one are we scratching?',
        choices: owned.map((id) => ({
          label: `${ITEMS[id].icon} ${ITEMS[id].name} (${store.count(id)})`,
          run: () => {
            window.setTimeout(() => this.scratchTicket(id), 0);
          },
        })),
        leave: 'Save them for later',
      });
      return;
    }
    const item = which ?? owned[0] ?? 'scratcher';
    if (!store.take(item)) {
      store.toast('No scratch-offs. Gas-N-Grub has 7s for $2.', 'bad', ':scratcher:');
      return;
    }
    store.incr('scratchers');
    const doubled = store.flag('jackpot_day') === store.day;
    const ticket = generateScratchTicket(rng, doubled ? 2 : 1, SCRATCH_GAMES[item]);
    ui.scratch.show(ticket, doubled, (prize) => {
      if (prize <= 0) return;
      store.earn(prize);
      store.incr('scratchWins');
      if (prize >= 1000) store.unlock('high_roller');
      if (prize >= 100) {
        store.incr('bigWins');
        store.headline(localHeadline('jackpot', store.state.player.name));
        store.post({
          author: store.state.player.name,
          avatar: 'player',
          text: `JUST WON $${prize} ON A SCRATCHER :slots::pray: god is real`,
          likes: rng.int(40, 120),
        });
      }
    });
  }

  lightFireworks(): void {
    const { store, player, rng, effects, npcs, ui } = this.s;
    if (!this.canAct() || this.driving.active) return;
    if (!store.take('fireworks')) {
      store.toast("No fireworks. Gas-N-Grub or Skeeter's trunk.", 'bad', ':fireworks:');
      return;
    }
    store.incr('fireworks');
    const night = isNight(store.minutes);
    if (!night) store.incr('daytimeFireworks');
    const f = player.facing;
    const x = player.position.x + Math.sin(f) * 2.5;
    const z = player.position.z + Math.cos(f) * 2.5;
    effects.launchFireworks(x, z, 6);
    this.s.share({ kind: 'fireworks', x, z });
    store.addClout(night ? 4 : 1);
    if (rng.chance(0.35)) store.headline(localHeadline('fireworks', store.state.player.name));
    const darlene = npcs.get('darlene');
    if (darlene?.active) {
      ui.hud.bubble(
        'darlene',
        night ? 'NO FIREWORKS IN THE PARK!! ...ooooh, pretty.' : "It's the middle of the DAY!",
        darlene.position.clone().setY(2.3),
        4,
      );
    }
  }

  /** Sit on a couch (the porch couch unless you say which), and crack one if you've got it. */
  sitOnCouch(at?: Spot): void {
    const { store, player } = this.s;
    if (!this.canAct()) return;
    const c = at ?? this.s.home().couch;
    player.teleport(c.x, c.z - 0.2, 0);
    const standUp = (): void => player.teleport(c.x, c.z + 0.9, 0);
    if (!store.has('beer')) {
      player.doBusy({
        pose: 'sit',
        seat: 0.5,
        until: this.s.now() + 2.5,
        facing: 0,
        onDone: () => {
          store.apply({ stats: { energy: 5 }, minutes: 15 });
          standUp();
        },
      });
      return;
    }
    store.take('beer');
    store.incr('beers');
    store.give('cans', 1);
    this.s.sfx('canOpen');
    player.doBusy({
      pose: 'sitdrink',
      seat: 0.5,
      until: this.s.now() + 3,
      facing: 0,
      onDone: () => {
        store.apply({ stats: { buzz: 16, energy: 6 }, clout: 1 });
        this.float('Ahhh. :beer:', '#ffd400');
        standUp();
        if (store.stat('buzz') >= 100) this.rest.passOut('blackout');
      },
    });
  }

  recline(): void {
    if (!this.canAct()) return;
    const home = this.s.home();
    const r = home.decor.recliner as Spot;
    const facing = home.facing(Math.PI / 2);
    this.s.player.teleport(r.x, r.z, facing);
    this.s.player.doBusy({
      pose: 'sit',
      seat: 0.5,
      until: this.s.now() + 3,
      facing,
      onDone: () => {
        this.s.store.apply({ stats: { energy: 12 }, minutes: 20 });
        // Step off toward the street.
        this.s.player.teleport(r.x - home.frame.side * 1.2, r.z, facing);
      },
    });
  }

  grill(spot: Spot, hunger: number): void {
    const { store, player, world } = this.s;
    if (!this.canAct()) return;
    // A fresh catfish beats a roller dog every time.
    const fish = store.take('catfish');
    if (!fish && !store.take('hotdog')) {
      store.toast(
        'Nothing to grill. Roller dogs at Gas-N-Grub, or noodle a catfish in the Back Forty.',
        'bad',
        ':hotdog:',
      );
      return;
    }
    store.incr('meals');
    player.facing = Math.atan2(spot.x - player.position.x, spot.z - player.position.z);
    for (let i = 0; i < 12; i++) {
      world.smoke.spawn({
        x: spot.x,
        y: 1.1,
        z: spot.z,
        vx: (Math.random() - 0.5) * 0.4,
        vy: 0.8 + Math.random() * 0.5,
        life: 2.5,
        size: 0.5,
        grow: 3,
        color: '#a8a8a2',
      });
    }
    this.s.sfx('crunch');
    player.doBusy({
      pose: 'stand',
      until: this.s.now() + 2.4,
      onDone: () => {
        store.apply({ stats: fish ? { hunger: hunger + 20, health: 5 } : { hunger } });
        this.float(fish ? ':catfish: Fish fry!' : ':hotdog: Perfection', '#ffa94d');
      },
    });
  }

  soak(kind: 'pool' | 'kiddie' | 'tub'): void {
    const { store, player, world } = this.s;
    if (!this.canAct()) return;
    const key = `soak_${kind}`;
    const lastSoak = Number(store.flag(key) ?? -999);
    if (store.now - lastSoak < 120) {
      store.toast("You're already pruney. Give it a couple hours.", 'info', ':drop:');
      return;
    }
    store.setFlag(key, store.now);
    const heat = store.flag('heatwave') === store.day;
    const slotId = kind === 'pool' ? 'pool' : kind === 'kiddie' ? 'kiddiePool' : 'tub';
    const home = this.s.home();
    const p = home.decor[slotId] as Spot;
    this.s.sfx('splash');
    for (let i = 0; i < 25; i++) {
      world.sparks.spawn({
        x: p.x,
        y: kind === 'pool' ? 1.1 : 0.4,
        z: p.z,
        vx: (Math.random() - 0.5) * 4,
        vy: 2 + Math.random() * 3,
        vz: (Math.random() - 0.5) * 4,
        life: 0.8,
        size: 0.14,
        color: '#9fdcff',
        gravity: 9,
      });
    }
    const back = { x: player.position.x, z: player.position.z };
    player.teleport(p.x, p.z, 0);
    if (kind === 'pool') player.y = 0.35;
    store.incr('swims');
    player.doBusy({
      pose: kind === 'pool' ? 'stand' : 'sit',
      seat: kind === 'tub' ? 0.42 : 0.18,
      until: this.s.now() + 3,
      facing: kind === 'tub' ? home.facing(Math.PI / 2) : 0,
      onDone: () => {
        player.y = 0;
        player.teleport(back.x, back.z);
        const base = kind === 'pool' ? 25 : kind === 'tub' ? 15 : 10;
        store.apply({
          stats: { energy: base + (heat ? 15 : 0), health: kind === 'kiddie' ? 0 : 5, buzz: -8 },
          minutes: 30,
        });
        this.float(heat ? ':sweat: SWEET RELIEF' : ':sweat: Refreshing', '#9fdcff');
      },
    });
  }

  checkMail(): void {
    const { store, rng, ui } = this.s;
    if (store.minutes < 10 * 60) {
      store.toast('Mail comes at 10 AM. The mailman fears Peanut.', 'info', ':mailbox:');
      return;
    }
    store.markToday('mail');
    this.s.sfx('door');
    const mail = drawMail(rng);
    ui.modal.show({
      title: "You've Got Mail",
      art: ':mailbox:',
      text: mail.text ?? '',
      choices: [],
      leave: 'Aight',
    });
    store.apply({ ...mail, text: undefined });
  }

  callMama(): void {
    const { store, rng, ui } = this.s;
    if (!store.spend(0.5)) {
      store.toast('Need 50 cents. Check the couch cushions.', 'bad', ':telephone:');
      return;
    }
    store.markToday('mama');
    this.s.sfx('phone');
    const lines = [
      "Mama says your cousin Ricky's in jail again, but 'the nice one this time.'",
      "Mama asks if you're eating. You say yes. You had a roller dog. She can hear the lie.",
      "Mama says she's praying for you. Specifically about 'that Camaro.'",
      "Mama tells you about her neighbor's gallbladder for 22 minutes. You feel loved.",
      `Mama says she saw a ${byBody(store.state.player.look, 'Florida Man', 'Florida Woman')} on the news who looked just like you. 'Was that you, baby?'`,
    ];
    ui.modal.show({
      title: 'Collect Call to Mama',
      art: ':telephone:',
      text: rng.pick(lines),
      choices: [],
      leave: 'Love you too, Mama',
    });
    store.apply({ stats: { energy: 5, health: 3 }, minutes: 20 });
  }

  dumpsterDive(): void {
    const { store, rng, ui } = this.s;
    store.markToday('dumpster');
    store.incr('dumpsterDives');
    this.s.sfx('clank');
    const roll = rng.next();
    let text: string;
    if (roll < 0.1 && !store.hasDecor('recliner')) {
      text = "You find a PERFECTLY GOOD recliner. One stain. Shaped like Texas. It's yours now.";
      store.apply({ decor: 'recliner' });
    } else if (roll < 0.35) {
      text = 'Half a pizza, still in the box. The box says "2 days ago." Close enough.';
      store.apply({ stats: { hunger: 25 } });
    } else {
      const n = rng.int(1, 5);
      text = `${n} cans and a raccoon who looks at you with professional respect.`;
      store.apply({ items: { cans: n } });
    }
    ui.modal.show({
      title: 'Dumpster Dive',
      art: ':trash-can:',
      text,
      choices: [],
      leave: "One man's trash",
    });
  }

  doLaundry(): void {
    const { store, ui } = this.s;
    if (!store.spend(3)) {
      store.toast('Need $3 in quarters.', 'bad', ':laundry:');
      return;
    }
    store.markToday('laundry');
    this.s.sfx('door');
    ui.modal.show({
      title: 'Suds-N-Duds',
      art: ':laundry:',
      text: 'You wash your one good outfit. You smell like a dryer sheet and confidence. A guy named Pickles tells you his life story while it spins.',
      choices: [],
      leave: 'Fresh',
    });
    store.apply({ clout: 2, minutes: 45 });
  }

  /** Watch TV: on the couch with the show on the set across the room, or the old way. */
  watchTv(room?: Room): void {
    const { store, ui, rng, player } = this.s;
    const screen = room?.tvScreenMesh;
    const couch = room?.furnitureAt('couch');
    if (screen && couch && this.canAct()) {
      const beer = store.take('beer');
      if (beer) {
        store.incr('beers');
        store.give('cans', 1);
        this.s.sfx('canOpen');
      }
      const stage = new TvStage(
        { screen, seat: { x: couch.x, z: couch.z - 0.05 }, facing: couch.rot, beer },
        player,
        () => pickShow(rng, store.hasDecor('dish')),
        rng,
        (n) => this.s.sfx(n),
      );
      ui.stages.open(stage, (st) => {
        const tv = st as TvStage;
        const per = store.hasDecor('bigTv') ? 2 : 1;
        store.incr('tv', tv.shows);
        if (tv.murray) store.incr('murray');
        store.apply({
          stats: { energy: Math.min(40, 10 * tv.shows * per), ...(beer ? { buzz: 16 } : {}) },
          minutes: 30 * tv.shows,
        });
        if (beer && store.stat('buzz') >= 100) window.setTimeout(() => this.rest.passOut('blackout'), 0);
        return null;
      });
      return;
    }
    ui.tv.show((shows, murray) => {
      const per = store.hasDecor('bigTv') ? 2 : 1;
      store.incr('tv', shows);
      if (murray) store.incr('murray');
      store.apply({ stats: { energy: Math.min(40, 10 * shows * per) }, minutes: 30 * shows });
    });
  }

  camaroAction(): void {
    const { store, player, world, ui, rng } = this.s;
    if (store.state.camaro >= 100) {
      this.driving.start();
      return;
    }
    if (!this.canAct()) return;
    if (!store.take('parts')) {
      store.toast("Need car parts. Junkyard Jerry sells 'em, $25 a pop.", 'bad', ':gear:');
      this.s.sfx('error');
      return;
    }
    store.incr('camaroWork');
    let clanks = 0;
    const clank = window.setInterval(() => {
      this.s.sfx('clank');
      if (++clanks >= 4) window.clearInterval(clank);
    }, 600);
    const car = this.s.world.rideRoot.position;
    player.facing = Math.atan2(car.x - player.position.x, car.z - player.position.z);
    player.doBusy({
      pose: 'push',
      until: this.s.now() + 2.8,
      onDone: () => {
        store.state.camaro = Math.min(100, store.state.camaro + 100 / CONFIG.camaroPartsNeeded);
        store.apply({ stats: { energy: -8 }, minutes: 60 });
        if (store.state.camaro < 100) {
          this.float(`:wrench: ${Math.round(store.state.camaro)}%`, '#ffd400');
          return;
        }
        world.setRide('camaro', false);
        this.s.sfx('airhorn');
        store.headline(localHeadline('camaro', store.state.player.name));
        store.post({
          author: store.state.player.name,
          avatar: 'player',
          text: 'SHE RUNS!!! :car::dash: after 17 years on blocks. haters said it couldnt be done',
          likes: rng.int(60, 140),
        });
        ui.modal.show({
          title: 'SHE RUNS!',
          art: ':car:',
          text: 'You turn the key. She coughs. She sputters. She ROARS. A flock of birds leaves the county. Earl drops his tongs and salutes. Your uncle, wherever he is, is proud.\n\nPress E at the Camaro to drive her. Or sell her to Junkyard Jerry for $1,200.',
          choices: [],
          leave: 'VROOM VROOM',
        });
      },
    });
  }
}
